import {
  memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, createElement,
} from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from 'react';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import {
  ArrowCounterclockwise,
  ChevronUp,
  ChevronDown,
} from 'react-bootstrap-icons';

import { Button } from '../buttons';

import DataTablePagingPanel from './DataTablePagingPanel';
import ColumnChooser from './DataTableV2ColumnChooser';
import DataTableV2CustomFooter from './DataTableV2CustomFooter';
import DataTableV2EmptyState from './DataTableV2EmptyState';
import HeaderFilter from './HeaderFilter';
import {
  handleCellTitle,
  measureColumnCell,
  renderCellValue,
  renderDefaultCellContent,
  textAlignStyle,
} from './dataTableV2Cell';
import {
  PAGE_SIZES,
  clampPage,
  filterRows,
  formatInt,
  getCellValue,
  resolveColumns,
  sortRows,
  sumColumn,
  visibleRange,
} from './dataTableV2Utils';
import type { ColumnFilter, InputColumn, ResolvedColumn } from './dataTableV2Utils';

import './DataTableV2.scss';

// Синхронизированы с DataTableV2.scss (th/td height) — менять вместе.
const ROW_HEIGHT = 40;
const HEADER_HEIGHT = 44;
// Высота виртуальной таблицы по умолчанию (как dx VirtualTable)
const DEFAULT_VIRTUAL_HEIGHT = 760;
// Ниже этого числа строк виртуализация не включается — прямой рендер дешевле
const VIRTUAL_THRESHOLD = 60;
// Пауза дебаунса быстрого поиска: страницы (Partners и др.) пишут searchValue
// на каждый символ — фильтрация больших данных мгновенно на каждый ввод бьёт
// по перфу; ввод остаётся мгновенным (инпут живёт на странице), фильтр
// срабатывает после паузы
const SEARCH_DEBOUNCE_MS = 200;
// Верхний кап авто-ширины колонки (двойной клик по хендлу ресайза)
const FIT_MAX_WIDTH = 600;
// Модель высот custom-темы — константы CustomDataTable (headerHeight 60, шапка
// в высоте бокса; maxTableHeight 700, minTableHeight 220)
const CUSTOM_HEADER_HEIGHT = 60;
const CUSTOM_MAX_TABLE_HEIGHT = 700;
const CUSTOM_MIN_TABLE_HEIGHT = 220;
// Скелетон первой загрузки: строки shimmer-полос в реальных колонках; ширины
// чередуем для живого вида, детерминантно (без миганий на ре-рендерах)
const SKELETON_BAR_WIDTHS = ['72%', '55%', '84%', '63%', '78%', '48%'] as const;
const SKELETON_ROWS_MIN = 3;
const SKELETON_ROWS_MAX = 12;

// Стабильные ссылки дефолтов (не создавать новые массивы на каждом рендере)
const EMPTY_DATA: any[] = [];
const EMPTY_HIDDEN_COLUMN_NAMES: string[] = [];
const EMPTY_WIDTHS: Record<string, number> = {};
const EMPTY_FILTERS: ColumnFilter[] = [];
// Дефолт пустого состояния при активном поиске/фильтрах; когда данных просто
// нет, DataTableV2EmptyState показывает «Нет данных» (кастомный noDataText
// страницы не трогаем — он для обеих ситуаций)
const DEFAULT_NO_DATA_TEXT = 'По Вашему запросу ничего не найдено';

/**
 * Одиночный шеврон сортировки: вверх = asc, вниз = desc, без сортировки —
 * полупрозрачный вверх. Единый вид для обеих тем (решение техлида 07.10 —
 * вместо dx-двойной стрелки с подсветкой активной).
 */
const SortChevron = ({ direction, size = 14 }: { direction: 'asc' | 'desc' | null; size?: number }) => {
  if (direction === 'desc') {
    return <ChevronDown size={ size } />;
  }

  return (
    <ChevronUp
      size={ size }
      style={ direction === 'asc' ? undefined : { opacity: 0.3 } }
    />
  );
};

interface HeaderCellProps {
  column: ResolvedColumn;
  /** Следующая видимая колонка — компенсирует ширину в режиме 'nextColumn'. */
  nextColumn: ResolvedColumn | null;
  resizingMode: 'nextColumn' | 'widget';
  sortDirection: 'asc' | 'desc' | null;
  showMultiFilter: boolean;
  /** Компактная шапка виртуальной таблицы — клон dx VirtualTableHeaderCell. */
  compact?: boolean;
  /**
   * custom-тема (CustomDataTable): одиночный шеврон сортировки справа, жирный
   * заголовок с выравниванием колонки, без хендла ресайза (легаси его не имел)
   * и без подсветки (инлайн-фон шапки перебивал bg-light — визуально её не было).
   */
  theme?: 'dx' | 'custom';
  onSort: (columnName: string) => void;
  onResizeCommit: (
    columnName: string,
    width: number,
    nextColumnName: string | null,
    nextWidth: number | null,
  ) => void;
}

/**
 * Ячейка шапки: сортировка кликом, мультифильтр (HeaderFilter), хендл ресайза.
 *
 * Ресайз мутирует style.width прямо в DOM (table-layout: fixed — ширина колонки
 * задаётся th) и фиксирует итог в state только на pointerup: во время drag
 * ререндеров нет вообще.
 */
const HeaderCell = memo(({
  column,
  nextColumn,
  resizingMode,
  sortDirection,
  showMultiFilter,
  compact = false,
  theme = 'dx',
  onSort,
  onResizeCommit,
}: HeaderCellProps) => {
  const thRef = useRef<HTMLTableCellElement>(null);

  // двойной клик по хендлу — автоподбор ширины по содержимому (как Excel/AG
  // Grid): меряем естественную ширину ячеек колонки (шапка + тело + суммы) —
  // колонка и сжимается, и растягивается
  const handleFitDoubleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const th = thRef.current;

    if (!th) {
      return;
    }

    const table = th.closest('table');
    const cellIndex = th.cellIndex;
    let measuredMax = 0;

    if (table) {
      table.querySelectorAll('tr').forEach((tr) => {
        const cell = (tr as HTMLTableRowElement).cells[cellIndex];

        if (cell) {
          measuredMax = Math.max(measuredMax, measureColumnCell(cell, cell.tagName === 'TH'));
        }
      });
    }

    // без реальных измерений (jsdom, скрытая таблица) ширину не трогаем
    if (measuredMax <= 0) {
      return;
    }

    const fit = Math.round(Math.max(
      column.resizeMinWidth,
      Math.min(measuredMax, column.maxWidth ?? FIT_MAX_WIDTH)
    ));

    onResizeCommit(column.name, fit, null, null);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    const th = thRef.current;
    const handle = event.currentTarget;

    if (!th) {
      return;
    }

    const startX = event.clientX;
    const startWidth = column.width;
    const nextTh = th.nextElementSibling as HTMLTableCellElement | null;
    const nextStartWidth = nextColumn?.width ?? 0;
    const nextMinWidth = nextColumn?.resizeMinWidth ?? 0;

    // в nextColumn-режиме рост колонки ограничен min-шириной соседа
    // (ресайз-минимум 45px по умолчанию — как dx, а не minWidth=width)
    const maxByNeighbour = resizingMode === 'nextColumn' && nextColumn
      ? startWidth + (nextStartWidth - nextMinWidth)
      : Number.POSITIVE_INFINITY;

    let finalWidth = startWidth;
    let finalNextWidth: number | null = null;
    let lastClientX = startX;
    let moved = false;
    let frame: number | undefined;

    handle.classList.add('is-active');
    handle.setPointerCapture(event.pointerId);

    const applyWidths = () => {
      frame = undefined;

      let width = startWidth + (lastClientX - startX);
      width = Math.max(column.resizeMinWidth, Math.min(width, maxByNeighbour));

      if (column.maxWidth != null) {
        width = Math.min(width, column.maxWidth);
      }

      finalWidth = Math.round(width);
      th.style.width = `${ finalWidth }px`;

      if (resizingMode === 'nextColumn' && nextColumn && nextTh) {
        finalNextWidth = Math.round(nextStartWidth - (finalWidth - startWidth));
        nextTh.style.width = `${ finalNextWidth }px`;
      }
    };

    const onPointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      moved = true;
      lastClientX = moveEvent.clientX;

      if (frame === undefined) {
        frame = requestAnimationFrame(applyWidths);
      }
    };

    const finish = (upEvent: PointerEvent) => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
        applyWidths();
      }

      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', finish);
      handle.removeEventListener('pointercancel', finish);
      handle.classList.remove('is-active');

      // клик по хендлу без движения не меняет ничего — state не трогаем
      if (moved) {
        onResizeCommit(
          column.name,
          finalWidth,
          nextColumn && finalNextWidth !== null ? nextColumn.name : null,
          finalNextWidth
        );
      }
    };

    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
  };

  const isCustomTheme = theme === 'custom';

  return (
    <th
      ref={ thRef }
      className={ classNames(
        'sortable',
        column.fixed && 'is-fixed',
        isCustomTheme && 'datatable-v2__th-custom'
      ) }
      style={ {
        width: column.width,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth ?? undefined,
        position: column.fixed ? 'sticky' : undefined,
        left: column.fixed ? column.stickyLeft : undefined,
        // custom: заголовок выравнивается как колонка (textAlign || right)
        ...(isCustomTheme ? textAlignStyle(column.align) : {}),
        // инлайн перебивает page-CSS (на страницах заказов th наследует 10px)
        ...(compact ? {
          fontSize: '12px',
          height: '30px',
          verticalAlign: 'middle' as CSSProperties['verticalAlign'],
          // виртуальная шапка залипает над скроллом и в легаси была НЕПРОЗРАЧНОЙ
          // (dx fixed-header на страницах заказов с белым thead) — иначе строки
          // просвечивают сквозь шапку при прокрутке
          backgroundColor: '#fff',
        } : {}),
      } }
      onClick={ () => onSort(column.name) }
    >
      { isCustomTheme ? (
        // клон шапки CustomDataTable: жирный заголовок слева + одиночный шеврон
        <div className='d-flex align-items-center justify-content-between'>
          <span className='font-weight-bold text-truncate' onMouseEnter={ handleCellTitle }>
            { column.title }
          </span>

          <span className='ml-2 d-flex align-items-center flex-shrink-0'>
            <SortChevron direction={ sortDirection } />
          </span>
        </div>
      ) : (
        <div className='d-flex align-items-center'>
          <span className='text-truncate' onMouseEnter={ handleCellTitle }>
            { column.title }
          </span>

          <span className='ml-1 d-flex align-items-center flex-shrink-0'>
            <SortChevron direction={ sortDirection } size={ compact ? 12 : 14 } />

            { /* воронка мультифильтра: клики не должны переключать сортировку */ }
            { showMultiFilter && column.options && (
              <span
                onClick={ event => event.stopPropagation() }
                role='presentation'
              >
                <HeaderFilter
                  title={ column.title }
                  options={ column.options }
                  value={ column.optionsValue }
                  onChange={ column.optionsFilter }
                />
              </span>
            ) }
          </span>
        </div>
      ) }

      { /* хендл ресайза — только dx-тема (CustomDataTable ресайза не имел) */ }
      { !isCustomTheme && (
        <div
          className='datatable-v2__resize-handle'
          onPointerDown={ handlePointerDown }
          // клик по хендлу не должен переключать сортировку шапки
          onClick={ event => event.stopPropagation() }
          onDoubleClick={ handleFitDoubleClick }
          role='presentation'
        />
      ) }
    </th>
  );
});

HeaderCell.displayName = 'DataTableV2HeaderCell';

interface BodyRowProps {
  row: any;
  rowId: number | string;
  columns: ResolvedColumn[];
  /** Активный (дебаунснутый) поисковый запрос — подсветка <mark> в ячейках. */
  highlightQuery?: string;
  rowClassName?: string | ((tableRow: any) => string | undefined | null);
  onRowClick: ((tableRow: any) => void) | null;
  cellComponent: ((props: any) => ReactNode) | null;
}

/**
 * Строка данных. td рендерятся из resolved-колонок: ширина берётся из th
 * (table-layout: fixed), sticky-left — только для закреплённых.
 *
 * Ключи элементов кастомных ячеек и children кастомных строк — `Symbol(data)_${name}`
 * (ключи dx-react-grid): страницы заказов определяют видимость колонок по этому ключу.
 */
const BodyRow = memo(({
  row,
  rowId,
  columns,
  highlightQuery = '',
  rowClassName,
  onRowClick,
  cellComponent,
}: BodyRowProps) => {
  const tableRow = useMemo(() => ({ rowId, row }), [rowId, row]);

  const className = typeof rowClassName === 'function' ? rowClassName(tableRow) : rowClassName;

  const handleClick = onRowClick
    ? (event: ReactMouseEvent<HTMLTableRowElement>) => {
      // dontRowClick — элементы, клик по которым не открывает строку (как DataTableRow)
      const target = event.target as HTMLElement;
      const targetClass = String(
        (target as any).className?.baseVal ?? (target as any).className ?? ''
      );

      if (targetClass.includes('dontRowClick')) {
        return;
      }

      onRowClick(tableRow);
    }
    : undefined;

  if (cellComponent) {
    return (
      <tr
        className={ classNames('data-row', onRowClick && 'clickable', className) }
        onClick={ handleClick }
      >
        { columns.map(column => createElement(cellComponent as any, {
          key: `Symbol(data)_${ column.name }`,
          row,
          column: column.source,
          tableRow,
          tableColumn: {
            column: column.source,
            width: column.width,
            minWidth: column.minWidth,
            align: column.align,
            fixed: column.fixed,
            wordWrapEnabled: column.wordWrapEnabled,
          },
          value: getCellValue(column.source, row),
        })) }
      </tr>
    );
  }

  return (
    <tr
      className={ classNames('data-row', onRowClick && 'clickable', className) }
      onClick={ handleClick }
    >
      { columns.map(column => (
        <td
          key={ column.name }
          className={ column.wordWrapEnabled ? 'word-wrap' : undefined }
          style={ {
            position: column.fixed ? 'sticky' : undefined,
            left: column.fixed ? column.stickyLeft : undefined,
            ...textAlignStyle(column.align),
          } }
          onMouseEnter={ handleCellTitle }
        >
          { renderDefaultCellContent(column, row, highlightQuery) }
        </td>
      )) }
    </tr>
  );
});

BodyRow.displayName = 'DataTableV2BodyRow';

/**
 * Таблица V2 — замена dx-react-grid и CustomDataTable без зависимостей от DevExpress.
 *
 * Покрывает живые фичи обоих движков (см. аудит 01.10): серверная/локальная
 * пагинация и сортировка, локальный поиск (searchValue, дебаунс 200мс +
 * подсветка <mark>) и фильтры панели (filters) с dx-семантикой
 * IntegratedFiltering (AND-фильтры + OR-поиск по всем колонкам, кастомный
 * filterPredicate колонки), скрытие столбцов + меню чузера, ресайз колонок
 * (widget/nextColumn, двойной клик по хендлу — автоподбор ширины по
 * содержимому), мультифильтр шапки, кастомные строки/ячейки с dx-совместимой
 * сигнатурой пропсов, закреплённые колонки (sticky), суммы showSum, итоговая
 * строка rowSummary (в custom-те липнет к низу прокрутки), тултип полного
 * значения у обрезанных ellipsis-ом ячеек, умное пустое состояние (иконка +
 * причина + кнопка сброса при активном поиске), виртуализация, скелетон первой
 * загрузки формой таблицы (реальная шапка + shimmer-строки) и fade-оверлей
 * повторных.
 *
 * Мёртвые фичи НЕ переносятся (проверено по всем потребителям, всё закомментировано):
 * showTableFilterRow, rowDetailComponent — пропы принимаются и игнорируются.
 * В custom-диалекте filters/searchValue игнорируются (CustomDataTable не фильтровал локально).
 */
export interface DataTableV2Props {
  loading?: boolean;
  data?: any[];
  /** Общее число записей (серверная пагинация); по умолчанию data.length. */
  dataLength?: number;
  columns: InputColumn[];
  /** @default 'widget' */
  columnsResizingMode?: 'nextColumn' | 'widget';
  /**
   * Сохранённые ширины колонок (px по имени) — стартовое значение ресайза
   * (обычно из localStorage-настроек страницы через DataTablePage).
   * Изменения пропа после маунта игнорируются — как defaultHiddenColumnNames.
   */
  defaultColumnWidths?: Record<string, number>;
  /** Коммит ресайза/автоширины: полная карта ширин; пустой объект = сброс к дефолтам (потребитель чистит хранилище). */
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  filters?: any[];
  /** Локальный поиск по значениям всех колонок (семантика dx SearchState). */
  searchValue?: string;
  currentPage?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  error?: { message?: string } | string | null;
  errorRetryHandler?: (...args: unknown[]) => unknown;
  hideToolbar?: boolean;
  hidePagination?: boolean;
  localSorting?: boolean;
  localPaging?: boolean;
  /** Кастомная строка: (rowProps) => ReactNode; rowProps = { row, tableRow, children, rowClassName, onRowClick }. */
  dataRowComponent?: (props: any) => ReactNode;
  /** Кастомная ячейка: (cellProps) => ReactNode; cellProps = { row, column, tableRow, tableColumn, value }. */
  dataCellComponent?: (props: any) => ReactNode;
  /** Рендер-проп содержимого тулбара (шестерёнка настроек из DataTablePage). */
  toolbarComponent?: (props: any) => ReactNode;
  rowClassName?: string | ((tableRow: any) => string | undefined | null);
  /** Итоговая строка в стиле dx SummaryState: [{ columnName, type, value }] (value считает потребитель). */
  rowSummary?: any[] | (() => any[]);
  defaultHiddenColumnNames?: string[];
  noDataText?: string;
  /** Кнопка «Сбросить» в пустом состоянии при активном поиске/фильтрах — стейт чистит страница. */
  onFiltersClear?: () => void;
  onSortingChange?: (columnName?: string, direction?: string) => void;
  /** Получает OFFSET (page * pageSize) — как у dx и CustomDataTable. */
  onCurrentPageChange?: (offset: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (tableRow: any) => void;
  onHideColumn?: (hiddenColumnNames: string[]) => void;
  height?: number;
  /** Показать таблицу со встроенным скроллом и виртуализацией. */
  showVirtualTable?: boolean;
  showTableFilterRow?: boolean;
  showTableMultiFilterRow?: boolean;
  isSettingsStored?: boolean;
  getRowId?: (row: any) => number | string;
  // — фичи CustomDataTable —
  keyboardScrollEnabled?: boolean;
  /** Колонки с showSum: строка сумм по ВСЕМ строкам data (не по странице). */
  shouldIncludeRowInSum?: (row: any, columnName?: string) => boolean;
  /** Рисовать таблицу строго суммой ширин колонок (без растяжения на 100%). */
  useColumnWidth?: boolean;
  /** Кап высоты скролл-области (px); undefined — таблица растёт по данным. */
  maxBodyHeight?: number;
  /** Дефолт выравнивания ячеек (диалект fixed-страниц: textAlign || 'right'). */
  defaultAlign?: string;
  /** Локальная сортировка по сырому row[name] вместо getCellValue (семантика CustomDataTable). */
  rawSortValues?: boolean;
  /**
   * Визуальная тема: 'dx' — «воздушный» bootstrap .table (клон старого dx-грида,
   * по умолчанию); 'custom' — «коробочный» вид CustomDataTable (fixed-страницы).
   */
  variant?: 'dx' | 'custom';
}

const DataTableV2 = (props: DataTableV2Props) => {
  const {
    loading = false,
    columns,
    data,
    dataLength,
    columnsResizingMode = 'widget',
    defaultColumnWidths,
    onColumnWidthsChange,
    error = null,
    errorRetryHandler,
    searchValue = '',
    filters,
    sortField,
    sortOrder,
    noDataText = DEFAULT_NO_DATA_TEXT,
    defaultHiddenColumnNames = EMPTY_HIDDEN_COLUMN_NAMES,
    dataRowComponent,
    dataCellComponent,
    toolbarComponent,
    rowClassName,
    rowSummary,
    hideToolbar = false,
    hidePagination = false,
    localSorting = false,
    localPaging = false,
    onCurrentPageChange,
    onPageSizeChange,
    onSortingChange,
    onRowClick,
    onHideColumn,
    onFiltersClear,
    height,
    showVirtualTable = false,
    showTableMultiFilterRow = false,
    isSettingsStored = false,
    getRowId,
    keyboardScrollEnabled = true,
    shouldIncludeRowInSum,
    useColumnWidth = false,
    maxBodyHeight,
    defaultAlign,
    rawSortValues = false,
    variant = 'dx',
  } = props;

  const safeColumns = useMemo(() => (Array.isArray(columns) ? columns : []), [columns]);
  const safeData = useMemo(() => (Array.isArray(data) ? data : EMPTY_DATA), [data]);
  const safeFilters = useMemo(() => (
    Array.isArray(filters) ? filters : EMPTY_FILTERS
  ), [filters]);

  // Стабилизация колбэков для memo-строк: consumer'ы передают inline-функции,
  // пересоздающиеся на каждом их рендере.
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;
  const handleRowClickStable = useCallback((tableRow: any) => {
    onRowClickRef.current?.(tableRow);
  }, []);

  const cellComponentRef = useRef(dataCellComponent);
  cellComponentRef.current = dataCellComponent;
  const stableCellComponent = useCallback(
    (cellProps: any) => cellComponentRef.current?.(cellProps) ?? null,
    []
  );

  const onSortingChangeRef = useRef(onSortingChange);
  onSortingChangeRef.current = onSortingChange;

  // — скрытие колонок —
  const initialHiddenColumnNames = useMemo(() => {
    const effective = [...defaultHiddenColumnNames];

    // custom-диалект не сеет visible:false в скрытые — CustomDataTable флаг игнорировал
    if (!isSettingsStored && variant !== 'custom') {
      safeColumns.forEach(column => {
        if (column?.visible === false && !effective.includes(column.name)) {
          effective.push(column.name);
        }
      });
    }

    return effective;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- начальное значение, как defaultHiddenColumnNames у dx
  }, []);

  const [hiddenColumnNames, setHiddenColumnNames] = useState<string[]>(initialHiddenColumnNames);
  const hiddenNamesSet = useMemo(() => new Set(hiddenColumnNames), [hiddenColumnNames]);

  const onHideColumnRef = useRef(onHideColumn);
  onHideColumnRef.current = onHideColumn;

  const handleToggleColumn = useCallback((columnName: string, visible: boolean) => {
    const next = visible
      ? hiddenColumnNames.filter(name => name !== columnName)
      : (hiddenColumnNames.includes(columnName) ? hiddenColumnNames : [...hiddenColumnNames, columnName]);

    setHiddenColumnNames(next);
    onHideColumnRef.current?.(next);
  }, [hiddenColumnNames]);

  // — ресайз —
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => (defaultColumnWidths && typeof defaultColumnWidths === 'object' ? defaultColumnWidths : EMPTY_WIDTHS)
  );

  // Зеркало стейта ширин + стабильный колбэк: handleResizeCommit не зависит от
  // стейта (стабилен для memo-HeaderCell) и зовёт onColumnWidthsChange один раз
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;
  const onColumnWidthsChangeRef = useRef(onColumnWidthsChange);
  onColumnWidthsChangeRef.current = onColumnWidthsChange;

  const handleResizeCommit = useCallback((
    columnName: string,
    width: number,
    nextColumnName: string | null,
    nextWidth: number | null
  ) => {
    const next = { ...columnWidthsRef.current, [columnName]: width };

    if (nextColumnName && nextWidth !== null) {
      next[nextColumnName] = nextWidth;
    }

    columnWidthsRef.current = next;
    setColumnWidths(next);
    onColumnWidthsChangeRef.current?.(next);
  }, []);

  // — сортировка —
  // стартовая (пропы маунта) — точка возврата для «Сбросить вид»
  const initialSorting = useMemo<{ columnName: string | null | undefined; direction: 'asc' | 'desc' }>(() => ({
    columnName: sortField,
    direction: (sortOrder?.toLowerCase() === 'desc' ? 'desc' : 'asc'),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- начальное значение, как sortField/sortOrder при маунте
  }), []);
  const [sorting, setSorting] = useState<{ columnName: string | null | undefined; direction: 'asc' | 'desc' }>(initialSorting);

  const handleSort = useCallback((columnName: string) => {
    const direction = sorting.columnName === columnName && sorting.direction === 'asc'
      ? 'desc'
      : 'asc';

    setSorting({ columnName, direction });
    onSortingChangeRef.current?.(columnName, direction.toUpperCase());
  }, [sorting]);

  /**
   * «Сбросить вид» (кнопка в тулбаре dx-темы): ширины, скрытые колонки и
   * сортировка — к стартовым дефолтам страницы. Сохранённые ширины потребитель
   * чистит по onColumnWidthsChange({}); скрытые и сортировка — внутренний
   * стейт, потребителей оповещаем onHideColumn/onSortingChange.
   */
  const handleResetView = useCallback(() => {
    if (Object.keys(columnWidthsRef.current).length > 0) {
      columnWidthsRef.current = EMPTY_WIDTHS;
      setColumnWidths(EMPTY_WIDTHS);
      onColumnWidthsChangeRef.current?.({});
    }

    const hiddenChanged = hiddenColumnNames.length !== initialHiddenColumnNames.length
      || hiddenColumnNames.some((name, index) => name !== initialHiddenColumnNames[index]);

    if (hiddenChanged) {
      setHiddenColumnNames(initialHiddenColumnNames);
      onHideColumnRef.current?.(initialHiddenColumnNames);
    }

    if (sorting.columnName !== initialSorting.columnName || sorting.direction !== initialSorting.direction) {
      setSorting({ ...initialSorting });
      onSortingChangeRef.current?.(initialSorting.columnName, initialSorting.direction.toUpperCase());
    }
  }, [hiddenColumnNames, initialHiddenColumnNames, sorting, initialSorting]);

  // — пагинация: внутренний стейт + синхронизация извне (сброс offset у страницы) —
  const [currentPage, setCurrentPage] = useState(
    typeof props.currentPage === 'number' && Number.isFinite(props.currentPage)
      ? Math.max(0, Math.trunc(props.currentPage))
      : 0
  );
  const [pageSize, setPageSize] = useState(
    typeof props.pageSize === 'number' && props.pageSize > 0 ? props.pageSize : 10
  );

  // Проп меняется извне (фильтр сбросил offset) → подтягиваем внутренний стейт.
  // Локальные клики проп не меняют (consumer может не эхоить offset) — поэтому
  // сравниваем с предыдущим значением ПРОПА, а не с внутренним стейтом.
  const prevPagePropRef = useRef(props.currentPage);
  useEffect(() => {
    if (props.currentPage !== prevPagePropRef.current) {
      prevPagePropRef.current = props.currentPage;

      if (typeof props.currentPage === 'number' && Number.isFinite(props.currentPage)) {
        setCurrentPage(Math.max(0, Math.trunc(props.currentPage)));
      }
    }
  }, [props.currentPage]);

  const prevSizePropRef = useRef(props.pageSize);
  useEffect(() => {
    if (props.pageSize !== prevSizePropRef.current) {
      prevSizePropRef.current = props.pageSize;

      if (typeof props.pageSize === 'number' && props.pageSize > 0) {
        setPageSize(props.pageSize);
      }
    }
  }, [props.pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    onCurrentPageChangeRef.current?.(page * pageSize);
  }, [pageSize]);
  const onCurrentPageChangeRef = useRef(onCurrentPageChange);
  onCurrentPageChangeRef.current = onCurrentPageChange;

  const handlePageSizeChange = useCallback((size: number) => {
    // смена размера страницы сбрасывает на первую (как dx/CustomDataTable)
    setPageSize(size);
    setCurrentPage(0);
    onPageSizeChangeRef.current?.(size);
    onCurrentPageChangeRef.current?.(0);
  }, []);
  const onPageSizeChangeRef = useRef(onPageSizeChange);
  onPageSizeChangeRef.current = onPageSizeChange;

  // — производные данные —
  const visibleColumns = useMemo(() => resolveColumns({
    columns: safeColumns,
    hiddenColumnNames,
    columnWidths,
    isSettingsStored,
    defaultAlign,
    ignoreVisibleFlag: variant === 'custom',
  }), [safeColumns, hiddenColumnNames, columnWidths, isSettingsStored, defaultAlign, variant]);

  // custom-диалект (CustomDataTable) не фильтровал локально ни по filters, ни по поиску
  const effectiveFilters = variant === 'custom' ? EMPTY_FILTERS : safeFilters;
  const effectiveSearchValue = variant === 'custom' ? '' : searchValue;

  // — дебаунс быстрого поиска —
  // Пустой запрос применяется сразу (снятие поиска не должно 200мс показывать
  // старую фильтрованную выборку), непустой — после паузы ввода.
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(effectiveSearchValue);

  useEffect(() => {
    if (effectiveSearchValue === '') {
      setDebouncedSearchValue((prev) => (prev === '' ? prev : ''));
      return undefined;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchValue(effectiveSearchValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [effectiveSearchValue]);

  // подсветка работает по тому же (дебаунснутому) запросу, что и фильтрация —
  // выделяем ровно то, что отфильтровано
  const highlightQuery = debouncedSearchValue.trim();

  // — умное пустое состояние —
  // Пусто «из-за запроса» = активен локальный поиск ИЛИ фильтры панели (dx-тема;
  // custom-диалект локально не фильтрует — там всегда «просто нет данных»).
  // Серверный поиск страниц V2 не видит — для них пусто остаётся «просто нет данных».
  const hasActiveQuery = effectiveSearchValue.trim() !== '' || effectiveFilters.length > 0;

  // Дефолтный текст про «запрос» уместен только при активном запросе; страницу
  // с кастомным noDataText не подменяем никогда
  const emptyTitle = !hasActiveQuery && noDataText === DEFAULT_NO_DATA_TEXT
    ? 'Нет данных'
    : noDataText;

  const filteredRows = useMemo(
    () => filterRows(safeData, safeColumns, effectiveFilters, debouncedSearchValue),
    [safeData, safeColumns, effectiveFilters, debouncedSearchValue]
  );

  const sortedRows = useMemo(() => (
    localSorting
      ? sortRows(filteredRows, safeColumns, sorting.columnName, sorting.direction, rawSortValues)
      : filteredRows
  ), [filteredRows, safeColumns, localSorting, sorting, rawSortValues]);

  const localTotalCount = filteredRows.length;
  const totalCount = localPaging
    ? localTotalCount
    : (dataLength ?? sortedRows.length);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const displayPage = clampPage(currentPage, totalPages);

  const pageRows = useMemo(() => (
    (localPaging && !hidePagination)
      ? sortedRows.slice(displayPage * pageSize, (displayPage + 1) * pageSize)
      : sortedRows
  ), [sortedRows, localPaging, hidePagination, displayPage, pageSize]);

  // — суммы (CustomDataTable showSum): по ВСЕМ строкам, исходные значения —
  const sumColumns = useMemo(
    () => safeColumns.filter(column => column?.showSum && column?.name),
    [safeColumns]
  );

  const sumValues = useMemo(() => {
    if (sumColumns.length === 0) {
      return {};
    }

    const values: Record<string, number> = {};
    sumColumns.forEach(column => {
      values[column.name] = sumColumn(safeData, column, shouldIncludeRowInSum);
    });
    return values;
  }, [sumColumns, safeData, shouldIncludeRowInSum]);

  const summaryItems = useMemo(() => (
    rowSummary ? (typeof rowSummary === 'function' ? rowSummary() : rowSummary) : null
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 1:1 с dx SummaryState (пересчёт по ссылке rowSummary)
  ), [rowSummary]);

  // — виртуализация —
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [measuredRowHeight, setMeasuredRowHeight] = useState(ROW_HEIGHT);
  const scrollFrameRef = useRef<number | undefined>(undefined);

  // первая загрузка: данных ещё нет — тело таблицы рисуем скелетоном той же
  // формы (реальная шапка + shimmer-строки в колонках): вёрстка не прыгает,
  // когда приходят данные. Оверлей-спиннер в этом режиме не нужен (индикатор —
  // сам скелетон), пагинация не рисуется до прихода данных.
  const isFirstLoad = loading && safeData.length === 0;
  const skeletonRowCount = Math.min(Math.max(pageSize, SKELETON_ROWS_MIN), SKELETON_ROWS_MAX);

  const effectiveMaxBodyHeight = showVirtualTable
    ? (height ?? DEFAULT_VIRTUAL_HEIGHT)
    : maxBodyHeight;

  // custom-тема: модель высот CustomDataTable — тело растёт со строками
  // (строка 40px + офсет скроллбара 35), кап 700-60, минимум 220-60 (minTableHeight);
  // на первой загрузке считаем по скелетон-строкам — бокс не «схлопывается»
  const contentRowCount = isFirstLoad
    ? skeletonRowCount
    : pageRows.length + (sumColumns.length > 0 && pageRows.length > 0 ? 1 : 0);

  const customBodyHeight = variant === 'custom'
    ? Math.max(
      Math.min(
        contentRowCount * ROW_HEIGHT + 35,
        CUSTOM_MAX_TABLE_HEIGHT - CUSTOM_HEADER_HEIGHT
      ),
      CUSTOM_MIN_TABLE_HEIGHT - CUSTOM_HEADER_HEIGHT
    )
    : null;

  const virtualized = Boolean(showVirtualTable) && pageRows.length > VIRTUAL_THRESHOLD;

  const handleScroll = useCallback(() => {
    if (scrollFrameRef.current !== undefined) {
      return;
    }

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = undefined;
      const el = scrollRef.current;

      if (el) {
        setScrollTop(el.scrollTop);
        setViewportHeight(el.clientHeight);
      }
    });
  }, []);

  useEffect(() => () => {
    if (scrollFrameRef.current !== undefined) {
      cancelAnimationFrame(scrollFrameRef.current);
    }
  }, []);

  // стартовая высота вьюпорта и измерение средней высоты строк
  // (кастомные ячейки заказов выше 40px — окно считается по факту)
  useLayoutEffect(() => {
    const el = scrollRef.current;

    if (!el || !virtualized) {
      return;
    }

    setViewportHeight(el.clientHeight);

    const rows = el.querySelectorAll('tr.data-row');

    if (rows.length < 2) {
      return;
    }

    const first = rows[0] as HTMLElement;
    const last = rows[rows.length - 1] as HTMLElement;
    const average = (last.getBoundingClientRect().bottom - first.getBoundingClientRect().top) / rows.length;

    if (Number.isFinite(average) && average > 0 && Math.abs(average - measuredRowHeight) > 1) {
      setMeasuredRowHeight(Math.round(average));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- пересчёт по смене данных; допуск >1px исключает осцилляции
  }, [pageRows, virtualized]);

  const virtualWindow = virtualized
    ? visibleRange(
      scrollTop,
      viewportHeight || Math.max(120, (effectiveMaxBodyHeight ?? DEFAULT_VIRTUAL_HEIGHT) - HEADER_HEIGHT),
      measuredRowHeight,
      pageRows.length
    )
    : { start: 0, end: pageRows.length };

  const windowRows = virtualized
    ? pageRows.slice(virtualWindow.start, virtualWindow.end)
    : pageRows;

  // — клавиатурный скролл —
  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const el = scrollRef.current;

    if (!el) {
      return;
    }

    const step = 50;
    const largeStep = 200;

    switch (event.key) {
      case 'ArrowLeft': event.preventDefault();el.scrollLeft -= step;break;
      case 'ArrowRight': event.preventDefault();el.scrollLeft += step;break;
      case 'ArrowUp': event.preventDefault();el.scrollTop -= step;break;
      case 'ArrowDown': event.preventDefault();el.scrollTop += step;break;
      case 'PageUp': event.preventDefault();el.scrollTop -= largeStep;break;
      case 'PageDown': event.preventDefault();el.scrollTop += largeStep;break;
      case 'Home':
        event.preventDefault();

        if (event.ctrlKey) {
          el.scrollLeft = 0;
        } else {
          el.scrollTop = 0;
        }

        break;
      case 'End':
        event.preventDefault();

        if (event.ctrlKey) {
          el.scrollLeft = el.scrollWidth;
        } else {
          el.scrollTop = el.scrollHeight - el.clientHeight;
        }

        break;
      default: break;
    }
  }, []);

  // — оверлей загрузки: fade-out 200ms, без миганий на коротких загрузках —
  const [overlayVisible, setOverlayVisible] = useState(false);

  useEffect(() => {
    if (loading) {
      setOverlayVisible(true);
      return undefined;
    }

    const timer = setTimeout(() => setOverlayVisible(false), 200);
    return () => clearTimeout(timer);
  }, [loading]);

  // — рендер —
  if (error && safeData.length === 0) {
    const errorMessage = typeof error === 'string' ? error : error.message;

    return (
      <div className='text-center text-danger p-4'>
        <div>Ошибка загрузки данных</div>

        { errorMessage && <div className='small'>{ errorMessage }</div> }

        { errorRetryHandler && (
          <Button
            variant='outline-danger'
            size='sm'
            className='mt-2'
            onClick={ errorRetryHandler }
          >
            Повторить
          </Button>
        ) }
      </div>
    );
  }

  const fromRow = displayPage * pageSize + 1;
  const toRow = Math.min((displayPage + 1) * pageSize, totalCount);

  const totalColumnsWidth = visibleColumns.reduce((sum, column) => sum + column.width, 0);

  // children кастомной строки: дефолтные td с ключами dx — по ним страницы
  // заказов определяют видимость колонок (hasColumn)
  const buildRowCells = (row: any) => visibleColumns.map(column => (
    <td
      key={ `Symbol(data)_${ column.name }` }
      className={ column.wordWrapEnabled ? 'word-wrap' : undefined }
      style={ {
        position: column.fixed ? 'sticky' : undefined,
        left: column.fixed ? column.stickyLeft : undefined,
        ...textAlignStyle(column.align),
      } }
      onMouseEnter={ handleCellTitle }
    >
      { renderDefaultCellContent(column, row, highlightQuery) }
    </td>
  ));

  const renderDataRow = (row: any, index: number) => {
    const absoluteIndex = (localPaging && !hidePagination)
      ? displayPage * pageSize + index
      : index;
    const rowId = getRowId ? getRowId(row) : absoluteIndex;
    const tableRow = { rowId, row };

    if (dataRowComponent) {
      // children в пропсах — контракт dx rowComponent: страницы заказов читают
      // ключи ячеек (Symbol(data)_name) для определения видимости колонок
      // eslint-disable-next-line react/no-children-prop
      return createElement(dataRowComponent as any, {
        key: rowId,
        row,
        tableRow,
        rowClassName,
        onRowClick: onRowClick ? handleRowClickStable : null,
        children: buildRowCells(row),
      });
    }

    return (
      <BodyRow
        key={ rowId }
        row={ row }
        rowId={ rowId }
        columns={ visibleColumns }
        highlightQuery={ highlightQuery }
        rowClassName={ rowClassName }
        onRowClick={ onRowClick ? handleRowClickStable : null }
        cellComponent={ dataCellComponent ? stableCellComponent : null }
      />
    );
  };

  // custom-тема: пустые данные — вместо таблицы блок-заглушка
  // (как у CustomDataTable: ни шапки, ни пагинации; рамка и иконка — наши)
  if (variant === 'custom' && pageRows.length === 0 && !loading) {
    return (
      <div className='border rounded p-3'>
        <DataTableV2EmptyState hasQuery={ false } noDataText={ emptyTitle } />
      </div>
    );
  }

  const scrollStyle = customBodyHeight != null
    // шапка 60px живёт внутри скролла (sticky) — добавляем её к телу,
    // чтобы итоговый бокс совпал с легаси (headerHeight + bodyHeight)
    ? { height: customBodyHeight + CUSTOM_HEADER_HEIGHT }
    : { maxHeight: effectiveMaxBodyHeight };

  return (
    <div className={ variant === 'custom' ? 'datatable-v2 datatable-v2--custom' : 'datatable-v2' }>
      { !hideToolbar && variant !== 'custom' && (
        <div
          className='card-header py-2 d-flex position-relative datatable-v2__toolbar row'
          style={ { backgroundColor: 'initial' } }
        >
          { totalCount > 0 && (
            <span>
              Найдено записей: { totalCount },&nbsp;показано с { fromRow } по { toRow }
            </span>
          ) }

          <div className='ml-auto d-flex align-items-center'>
            { toolbarComponent ? toolbarComponent({
              dataLength: totalCount,
              currentPage: displayPage,
              pageSize,
            }) : null }

            <ColumnChooser
              columns={ safeColumns }
              hiddenNames={ hiddenNamesSet }
              onToggleColumn={ handleToggleColumn }
            />

            { /* сброс ширины/скрытых колонок/сортировки к дефолтам страницы */ }
            <Button
              variant='outline-secondary'
              className='border-0'
              title='Сбросить вид таблицы'
              onClick={ handleResetView }
            >
              <ArrowCounterclockwise />
            </Button>
          </div>
        </div>
      ) }

      { /* оверлей загрузки накрывает таблицу и пагинацию (клики блокируются) */ }
      <div style={ { position: 'relative' } }>
        { /* custom-тема: бокс с рамкой (шапка+тело внутри, футер вне бокса) */ }
        <div className={ variant === 'custom' ? 'datatable-v2__box' : undefined }>
          <div
            ref={ scrollRef }
            className='datatable-v2__scroll'
            style={ scrollStyle }
            tabIndex={ keyboardScrollEnabled ? 0 : undefined }
            onKeyDown={ keyboardScrollEnabled ? handleKeyDown : undefined }
            onScroll={ virtualized ? handleScroll : undefined }
          >
            <table
              className='datatable-v2__table'
              style={ {
                width: useColumnWidth ? totalColumnsWidth : undefined,
                minWidth: totalColumnsWidth,
              } }
              aria-busy={ isFirstLoad || undefined }
            >
              <thead>
                <tr>
                  { visibleColumns.map((column, index) => (
                    <HeaderCell
                      key={ column.name }
                      column={ column }
                      nextColumn={ visibleColumns[index + 1] ?? null }
                      resizingMode={ columnsResizingMode }
                      sortDirection={ sorting.columnName === column.name ? sorting.direction : null }
                      showMultiFilter={ showTableMultiFilterRow }
                      compact={ showVirtualTable }
                      theme={ variant }
                      onSort={ handleSort }
                      onResizeCommit={ handleResizeCommit }
                    />
                  )) }
                </tr>
              </thead>

              { isFirstLoad ? (
                <tbody>
                  { Array.from({ length: skeletonRowCount }, (_, index) => (
                    <tr
                      className='datatable-v2__skeleton-row'
                      key={ `skeleton-${ index }` }
                      aria-hidden='true'
                    >
                      { visibleColumns.map((column, colIndex) => (
                        <td key={ column.name }>
                          <Skeleton
                            height={ 12 }
                            width={ SKELETON_BAR_WIDTHS[colIndex % SKELETON_BAR_WIDTHS.length] }
                          />
                        </td>
                      )) }
                    </tr>
                  )) }
                </tbody>
              ) : (
                <tbody>
                  { virtualized && virtualWindow.start > 0 && (
                    <tr aria-hidden='true' style={ { height: virtualWindow.start * measuredRowHeight } }>
                      <td colSpan={ visibleColumns.length } style={ { padding: 0, border: 'none' } } />
                    </tr>
                  ) }

                  { windowRows.map((row, index) => renderDataRow(
                    row,
                    virtualized ? virtualWindow.start + index : index
                  )) }

                  { virtualized && virtualWindow.end < pageRows.length && (
                    <tr aria-hidden='true' style={ { height: (pageRows.length - virtualWindow.end) * measuredRowHeight } }>
                      <td colSpan={ visibleColumns.length } style={ { padding: 0, border: 'none' } } />
                    </tr>
                  ) }

                  { sumColumns.length > 0 && pageRows.length > 0 && (
                    <tr className='sum-row'>
                      { visibleColumns.map(column => (
                        <td key={ column.name } style={ textAlignStyle(column.align) }>
                          { column.source.showSum ? formatInt(sumValues[column.name]) : '' }
                        </td>
                      )) }
                    </tr>
                  ) }

                  { summaryItems && summaryItems.length > 0 && pageRows.length > 0 && (
                    <tr className='sum-row'>
                      { visibleColumns.map((column) => {
                        const item = summaryItems.find((summary: any) => summary.columnName === column.name);

                        return (
                          <td key={ column.name } style={ textAlignStyle(column.align) }>
                            { item ? `${ item.type === 'count' ? 'Кол-во' : 'Итого' }: ${ item.value }` : '' }
                          </td>
                        );
                      }) }
                    </tr>
                  ) }

                  { pageRows.length === 0 && !loading && (
                    <tr className='datatable-v2__no-data'>
                      <td colSpan={ visibleColumns.length }>
                        <DataTableV2EmptyState
                          hasQuery={ hasActiveQuery }
                          noDataText={ emptyTitle }
                          onFiltersClear={ onFiltersClear }
                        />
                      </td>
                    </tr>
                  ) }
                </tbody>
              ) }
            </table>
          </div>
        </div>

        { variant === 'custom' ? (
          /* футер custom-темы — клон CustomDataTable (вне рамки, только с данными;
             на первой загрузке не рисуем — вместе с ним появится после данных) */
          !hidePagination && !isFirstLoad && totalCount > 0 && (
            <DataTableV2CustomFooter
              pageSize={ pageSize }
              pageSizes={ PAGE_SIZES }
              currentPage={ displayPage }
              totalPages={ totalPages }
              totalCount={ totalCount }
              onPageChange={ handlePageChange }
              onPageSizeChange={ handlePageSizeChange }
            />
          )
        ) : (
          /* футер — тот же компонент, что рендерил dx-PagingPanel: пагинация
             float-start + компактный селектор размера float-end на той же строке */
          !hidePagination && !isFirstLoad && (
            <DataTablePagingPanel
              totalPages={ totalPages }
              currentPage={ displayPage }
              onCurrentPageChange={ handlePageChange }
              pageSize={ pageSize }
              pageSizes={ PAGE_SIZES }
              onChange={ handlePageSizeChange }
            />
          )
        ) }

        { overlayVisible && !isFirstLoad && (
          <div className={ classNames('datatable-v2__overlay', !loading && 'is-hidden') }>
            <div className='spinner-border text-primary' role='status'>
              <span className='sr-only'>Загрузка...</span>
            </div>
          </div>
        ) }
      </div>
    </div>
  );
};

export default DataTableV2;
